<template>
  <div class="subinfo-page common-bg">
    <div class="subinfo-filter-bar">
      <div class="subinfo-filters">
        <div class="subinfo-filter" @click="toggleDateDropdown">
          <span>{{ dateFilterText }}</span>
          <van-icon :name="showDateDropdown ? 'arrow-up' : 'arrow-down'" size="12" />
        </div>
        <div class="subinfo-filter" @click="toggleSortDropdown">
          <span>{{ sortFilterText }}</span>
          <van-icon :name="showSortDropdown ? 'arrow-up' : 'arrow-down'" size="12" />
        </div>
      </div>
      <div class="subinfo-search">
        <input
          v-model="searchId"
          type="text"
          placeholder="会员ID"
          class="subinfo-search-input"
          @keyup.enter="handleSearch"
        />
        <van-icon name="search" size="16" color="#888" class="subinfo-search-icon" @click="handleSearch" />
      </div>

      <!-- 日期筛选悬浮弹框（参考 /withdraw?active=3 提现记录弹框样式） -->
      <div class="subinfo-date-dropdown" v-if="showDateDropdown">
        <div class="subinfo-date-options">
          <div
            class="subinfo-date-option"
            v-for="(item, index) in dateOptionList"
            :key="item.value"
            :class="{ active: dateFilter === item.value }"
            @click="selectDateFilter(item.value)"
          >{{ item.text }}</div>
        </div>
        <template v-if="dateFilter !== 'all'">
          <div class="subinfo-date-custom-label">自定义</div>
          <div class="subinfo-date-range">
            <div class="subinfo-date-range-col">
              <div class="subinfo-date-range-title">开始日期</div>
              <div class="subinfo-date-picker">
                <div class="subinfo-date-picker-col">
                  <ul class="subinfo-date-picker-list" :style="{ transform: startYearTransform }">
                    <li
                      class="subinfo-date-picker-item"
                      v-for="y in startYearWindow"
                      :key="'sy'+y"
                      :class="getItemClass(startDate[0], y, yearList)"
                      @click="selectStartDate(0, y)"
                    >{{ y }}</li>
                  </ul>
                </div>
                <div class="subinfo-date-picker-col">
                  <ul class="subinfo-date-picker-list" :style="{ transform: startMonthTransform }">
                    <li
                      class="subinfo-date-picker-item"
                      v-for="m in startMonthWindow"
                      :key="'sm'+m"
                      :class="getItemClass(startDate[1], m, monthList)"
                      @click="selectStartDate(1, m)"
                    >{{ String(m).padStart(2, '0') }}</li>
                  </ul>
                </div>
                <div class="subinfo-date-picker-col">
                  <ul class="subinfo-date-picker-list" :style="{ transform: startDayTransform }">
                    <li
                      class="subinfo-date-picker-item"
                      v-for="d in startDayWindow"
                      :key="'sd'+d"
                      :class="getItemClass(startDate[2], d, dayList)"
                      @click="selectStartDate(2, d)"
                    >{{ String(d).padStart(2, '0') }}</li>
                  </ul>
                </div>
                <div class="subinfo-date-picker-mask"></div>
                <div class="subinfo-date-picker-frame"></div>
              </div>
            </div>
            <div class="subinfo-date-range-col">
              <div class="subinfo-date-range-title">结束日期</div>
              <div class="subinfo-date-picker">
                <div class="subinfo-date-picker-col">
                  <ul class="subinfo-date-picker-list" :style="{ transform: endYearTransform }">
                    <li
                      class="subinfo-date-picker-item"
                      v-for="y in endYearWindow"
                      :key="'ey'+y"
                      :class="getItemClass(endDate[0], y, endYearList)"
                      @click="selectEndDate(0, y)"
                    >{{ y }}</li>
                  </ul>
                </div>
                <div class="subinfo-date-picker-col">
                  <ul class="subinfo-date-picker-list" :style="{ transform: endMonthTransform }">
                    <li
                      class="subinfo-date-picker-item"
                      v-for="m in endMonthWindow"
                      :key="'em'+m"
                      :class="getItemClass(endDate[1], m, endMonthList)"
                      @click="selectEndDate(1, m)"
                    >{{ String(m).padStart(2, '0') }}</li>
                  </ul>
                </div>
                <div class="subinfo-date-picker-col">
                  <ul class="subinfo-date-picker-list" :style="{ transform: endDayTransform }">
                    <li
                      class="subinfo-date-picker-item"
                      v-for="d in endDayWindow"
                      :key="'ed'+d"
                      :class="getItemClass(endDate[2], d, endDayList)"
                      @click="selectEndDate(2, d)"
                    >{{ String(d).padStart(2, '0') }}</li>
                  </ul>
                </div>
                <div class="subinfo-date-picker-mask"></div>
                <div class="subinfo-date-picker-frame"></div>
              </div>
            </div>
          </div>
          <div class="subinfo-date-btns">
            <div class="subinfo-date-btn subinfo-date-btn-cancel" @click="showDateDropdown = false">取消</div>
            <div class="subinfo-date-btn subinfo-date-btn-confirm" @click="confirmDate">确认</div>
          </div>
        </template>
      </div>

      <!-- 排序筛选悬浮弹框（同样参考提现记录的状态下拉：短、窄、列表式） -->
      <div class="subinfo-sort-dropdown" v-if="showSortDropdown">
        <div
          class="subinfo-sort-option"
          v-for="(item, index) in sortOptionList"
          :key="item.value"
          :class="{ active: sortFilter === item.value }"
          @click="selectSort(item.value)"
        >{{ item.text }}</div>
      </div>
    </div>

    <div class="list-wrap" ref="listWrap" @scroll="onScroll">
      <div class="sub-list" v-if="subList.length > 0">
        <div class="sub-item" v-for="(item, index) in subList" :key="item.userId + '_' + index">
          <div class="sub-top">
            <div class="sub-left">
              <div class="avatar-wrap">
                <img :src="getAvatarUrl(item)" class="avatar" alt="" @error="onAvatarError" />
                <div class="vip-badge">V{{ item.vipLevel }}</div>
              </div>
              <div class="sub-info">
                <div class="sub-id">
                  <span class="id-text">ID{{ item.userId }}</span>
                  <span class="status-dot" :class="{ online: item.isOnline }"></span>
                  <span class="status-text">{{ item.isOnline ? '在线' : '离线' }}</span>
                </div>
                <div class="sub-row">
                  <span class="sub-label">会员</span>
                  <span class="sub-value">{{ item.username }}</span>
                </div>
                <div class="sub-row">
                  <span class="sub-label">充值金额</span>
                  <span class="sub-value">{{ item.rechargeAmount }}</span>
                </div>
                <div class="sub-row">
                  <span class="sub-label">注册时间</span>
                  <span class="sub-value register-time">{{ item.registerDate }}</span>
                </div>
              </div>
            </div>
            <div class="sub-right">
              <div class="right-row">
                <span class="label">他的下级</span>
                <span class="value">{{ item.subCount }}</span>
              </div>
              <div class="right-row">
                <span class="label">有效投注</span>
                <span class="value">{{ item.validBet }}</span>
              </div>
              <div class="right-row">
                <span class="label">状态</span>
                <span
                  class="value"
                  :class="(item.statusCode === 1 || item.status === '正常') ? 'normal' : 'ban'"
                >{{ item.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="list-footer">
        <div class="load-more" v-if="subList.length > 0 && page < lastPage && !loading" @click="loadMore">
          <span class="lm-text">点击加载更多</span>
        </div>
        <div class="list-tip" v-else-if="loading">加载中...</div>
        <div class="list-tip" v-else-if="subList.length > 0 && page >= lastPage">没有更多了</div>
      </div>

      <div class="empty-state" v-if="!loading && subList.length === 0">
        <img src="/static/image/img_none_sj.avif" class="empty-icon" alt="" />
        <p class="empty-text">今日暂无记录，<span class="empty-link-gray">可</span><span class="empty-link">查看更多</span></p>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'promoteSubInfo',
  data() {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    const d = today.getDate();
    return {
      // 日期筛选：跟提现记录一致 today/yesterday/week/lastweek/month/lastmonth/all/custom
      dateFilter: 'all',
      // 排序筛选：0登录日期 1注册日期 2充值金额
      sortFilter: 0,
      searchId: '',
      showDateDropdown: false,
      showSortDropdown: false,
      startDate: [y, m, d],
      endDate:   [y, m, d],
      customStartTs: null,
      customEndTs:   null,
      // 选项配置（一行 3 个：第1行今日/昨日/本周，第2行上周/本月/上月，第3行全部=1/3宽）
      dateOptionList: [
        { text: '今日',  value: 'today' },
        { text: '昨日',  value: 'yesterday' },
        { text: '本周',  value: 'week' },
        { text: '上周',  value: 'lastweek' },
        { text: '本月',  value: 'month' },
        { text: '上月',  value: 'lastmonth' },
        { text: '全部',  value: 'all' },
      ],
      sortOptionList: [
        { text: '登录日期排序', value: 0 },
        { text: '注册日期排序', value: 1 },
        { text: '充值金额排序', value: 2 },
      ],
      itemHeight: 0.6,
      subList: [],
      page: 1,
      lastPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      directTotal: 0,
      teamTotal: 0,
    };
  },
  computed: {
    dateFilterText() {
      if (this.dateFilter === 'custom') {
        const s = `${this.startDate[0]}-${String(this.startDate[1]).padStart(2, '0')}-${String(this.startDate[2]).padStart(2, '0')}`;
        const e = `${this.endDate[0]}-${String(this.endDate[1]).padStart(2, '0')}-${String(this.endDate[2]).padStart(2, '0')}`;
        return `${s} ~ ${e}`;
      }
      const map = {
        today: '今日',
        yesterday: '昨日',
        week: '本周',
        lastweek: '上周',
        month: '本月',
        lastmonth: '上月',
        all: '全部',
      };
      return map[this.dateFilter] || '全部';
    },
    sortFilterText() {
      const hit = this.sortOptionList.find((o) => o.value === this.sortFilter);
      return hit ? hit.text : '登录日期排序';
    },
    yearList() {
      const now = new Date().getFullYear();
      const list = [];
      for (let y = now - 5; y <= now + 5; y++) list.push(y);
      return list;
    },
    endYearList() {
      const now = new Date();
      const list = [];
      for (let y = now.getFullYear() - 5; y <= now.getFullYear(); y++) list.push(y);
      return list;
    },
    monthList() {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    },
    endMonthList() {
      const now = new Date();
      if (this.endDate[0] < now.getFullYear()) return this.monthList;
      return Array.from({ length: now.getMonth() + 1 }, (_, i) => i + 1);
    },
    dayList() {
      return Array.from({ length: 31 }, (_, i) => i + 1);
    },
    endDayList() {
      const now = new Date();
      if (this.endDate[0] < now.getFullYear() || this.endDate[1] < now.getMonth() + 1) {
        return this.dayList;
      }
      return Array.from({ length: now.getDate() }, (_, i) => i + 1);
    },
    startYearWindow()  { return this.getWindow(this.yearList,   this.startDate[0]); },
    startMonthWindow() { return this.getWindow(this.monthList,  this.startDate[1]); },
    startDayWindow()   { return this.getWindow(this.dayList,    this.startDate[2]); },
    endYearWindow()    { return this.getWindow(this.endYearList,  this.endDate[0]); },
    endMonthWindow()   { return this.getWindow(this.endMonthList, this.endDate[1]); },
    endDayWindow()     { return this.getWindow(this.endDayList,   this.endDate[2]); },
    startYearTransform()  { return this.computeWindowTransform(this.startYearWindow,  this.startDate[0]); },
    startMonthTransform() { return this.computeWindowTransform(this.startMonthWindow, this.startDate[1]); },
    startDayTransform()   { return this.computeWindowTransform(this.startDayWindow,   this.startDate[2]); },
    endYearTransform()    { return this.computeWindowTransform(this.endYearWindow,    this.endDate[0], this.endYearList); },
    endMonthTransform()   { return this.computeWindowTransform(this.endMonthWindow,   this.endDate[1], this.endMonthList); },
    endDayTransform()     { return this.computeWindowTransform(this.endDayWindow,     this.endDate[2], this.endDayList); },
  },
  created() {
    this.loadData();
  },
  methods: {
    // ===== 日期弹框工具：跟提现记录完全一致的 3 列滚动窗口 =====
    getWindow(list, selectedVal) {
      const idx = list.indexOf(selectedVal);
      const items = [];
      if (idx > 0) items.push(list[idx - 1]);
      items.push(selectedVal);
      if (idx < list.length - 1) items.push(list[idx + 1]);
      return items;
    },
    computeWindowTransform(window, selectedVal, list) {
      const idx = window.indexOf(selectedVal);
      const offset = 0.9 - idx * this.itemHeight;
      return `translateY(${offset}rem)`;
    },
    getItemClass(val, item, fullList) {
      if (val === item) return 'selected';
      // 窗口内不可点的条目（不在整表范围内不展示选中即可）
      if (fullList && fullList.indexOf(item) === -1) return '';
      return '';
    },

    // ===== 切换弹框：打开一个自动关另一个 =====
    toggleDateDropdown() {
      this.showDateDropdown = !this.showDateDropdown;
      if (this.showDateDropdown) this.showSortDropdown = false;
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
      if (this.showSortDropdown) this.showDateDropdown = false;
    },

    // ===== 日期选项点击 =====
    selectDateFilter(filter) {
      this.dateFilter = filter;
      this.customStartTs = null;
      this.customEndTs   = null;
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth() + 1;
      const d = now.getDate();
      const toTs = (yy, mm, dd, endOfDay = false) => {
        const dt = new Date(yy, mm - 1, dd, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, 0);
        return Math.floor(dt.getTime() / 1000);
      };
      const set = (sy, sm, sd, ey, em, ed) => {
        this.startDate = [sy, sm, sd];
        this.endDate   = [ey, em, ed];
        this.customStartTs = toTs(sy, sm, sd);
        this.customEndTs   = toTs(ey, em, ed, true);
      };
      switch (filter) {
        case 'today':
          set(y, m, d, y, m, d);
          break;
        case 'yesterday': {
          const ys = new Date(now.getTime() - 86400000);
          set(ys.getFullYear(), ys.getMonth() + 1, ys.getDate(), ys.getFullYear(), ys.getMonth() + 1, ys.getDate());
          break;
        }
        case 'week': {
          const day = (now.getDay() + 6) % 7; // 周一为 0
          const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
          const sunday = new Date(monday.getTime() + 6 * 86400000);
          set(monday.getFullYear(), monday.getMonth() + 1, monday.getDate(),
              sunday.getFullYear(), sunday.getMonth() + 1, sunday.getDate());
          break;
        }
        case 'lastweek': {
          const day = (now.getDay() + 6) % 7;
          const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day - 7);
          const sunday = new Date(monday.getTime() + 6 * 86400000);
          set(monday.getFullYear(), monday.getMonth() + 1, monday.getDate(),
              sunday.getFullYear(), sunday.getMonth() + 1, sunday.getDate());
          break;
        }
        case 'month':
          set(y, m, 1, y, m, new Date(y, m, 0).getDate());
          break;
        case 'lastmonth': {
          const lmY = m === 1 ? y - 1 : y;
          const lmM = m === 1 ? 12 : m - 1;
          set(lmY, lmM, 1, lmY, lmM, new Date(lmY, lmM, 0).getDate());
          break;
        }
        case 'all':
        default:
          // 「全部」直接关弹框并刷新
          this.showDateDropdown = false;
          this.page = 1;
          this.loadData();
          return;
      }
      // 非 all：等用户点击确认或继续切，这里不自动请求
    },
    selectStartDate(idx, val) {
      const next = this.startDate.slice();
      next[idx] = val;
      // 避免日期越界（比如切换到小月 31 日）
      const maxDay = new Date(next[0], next[1], 0).getDate();
      if (idx === 2 && next[2] > maxDay) next[2] = maxDay;
      this.startDate = next;
    },
    selectEndDate(idx, val) {
      const next = this.endDate.slice();
      next[idx] = val;
      const maxDay = new Date(next[0], next[1], 0).getDate();
      if (idx === 2 && next[2] > maxDay) next[2] = maxDay;
      this.endDate = next;
    },
    confirmDate() {
      this.dateFilter = 'custom';
      const toTs = (yy, mm, dd, endOfDay = false) => {
        const dt = new Date(yy, mm - 1, dd, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, 0);
        return Math.floor(dt.getTime() / 1000);
      };
      this.customStartTs = toTs(this.startDate[0], this.startDate[1], this.startDate[2]);
      this.customEndTs   = toTs(this.endDate[0],   this.endDate[1],   this.endDate[2], true);
      this.showDateDropdown = false;
      this.page = 1;
      this.loadData();
    },

    // ===== 排序选项点击（跟提现记录 selectStatus 行为一致：点击即关弹框 + 刷新） =====
    selectSort(val) {
      this.sortFilter = val;
      this.showSortDropdown = false;
      this.page = 1;
      this.loadData();
    },

    // ===== 搜索 =====
    handleSearch() {
      this.page = 1;
      this.loadData();
    },
    onScroll(e) {
      const el = e && e.target;
      if (!el) return;
      const remain = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remain < 80 && !this.loading && this.page < this.lastPage) {
        this.loadMore();
      }
    },
    loadMore() {
      if (this.loading || this.page >= this.lastPage) return;
      this.page++;
      this.loadData(true);
    },
    // 把 dateFilter 字符串映射成后端 getTeamList 需要的数字 date_type
    computeDateType() {
      const map = {
        today: 0, yesterday: 1, week: 2, lastweek: 3, month: 4, lastmonth: 5, all: 6, custom: 6,
      };
      return Object.prototype.hasOwnProperty.call(map, this.dateFilter) ? map[this.dateFilter] : 6;
    },
    loadData(append) {
      let that = this;
      if (that.loading) return;
      that.loading = true;
      if (!append) that.showLoading && that.showLoading();

      that.$apiFun
        .getTeamList({
          page:       that.page,
          page_size:  that.pageSize,
          search:     (that.searchId || '').trim(),
          date_type:  that.computeDateType(),
          sort_type:  Number(that.sortFilter) === 0 ? 0 : (Number(that.sortFilter) || 0),
          date_field: Number(that.sortFilter) === 1 ? 'reg' : 'login',
          // 自定义范围：后端 getTeamList 目前没有 start_ts / end_ts，但我们一起传过去，
          // 后端若未支持，会被忽略；要支持直接读这俩字段就是精确范围。
          start_ts:   that.dateFilter === 'custom' ? (that.customStartTs || 0) : 0,
          end_ts:     that.dateFilter === 'custom' ? (that.customEndTs   || 0) : 0,
        })
        .then((res) => {
          that.loading = false;
          that.hideLoading && that.hideLoading();
          if (res && res.code === 200) {
            const data = res.data || {};
            if (append) {
              that.subList = that.subList.concat(data.data || []);
            } else {
              that.subList = data.data || [];
            }
            that.total    = data.total    || 0;
            that.lastPage = data.last_page || data.lastPage || 1;
            that.page     = data.current_page || that.page;
            that.directTotal = data.direct_total || 0;
            that.teamTotal   = data.team_total   || 0;
          } else if (res && res.code === 401) {
            that.showGlobalTost(0, (res && res.message) || '请先登录');
            if (!append) that.subList = [];
          } else {
            that.showGlobalTost(0, (res && res.message) || '加载失败');
            if (!append) that.subList = [];
          }
        })
        .catch((err) => {
          that.loading = false;
          that.hideLoading && that.hideLoading();
          console.error('getTeamList error:', err);
          that.showGlobalTost(0, '网络异常，请重试');
          if (!append) that.subList = [];
        });
    },
    showGlobalTost(type, title) {
      if (this.$parent && typeof this.$parent.showTost === 'function') {
        this.$parent.showTost(type, title);
        return;
      }
      let p = this.$parent;
      while (p) {
        if (typeof p.showTost === 'function') {
          p.showTost(type, title);
          return;
        }
        p = p.$parent;
      }
      try {
        this.$toast && this.$toast({ message: title || '', position: 'middle' });
      } catch (e) {}
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    getAvatarUrl(item) {
      const DEFAULT_AVATAR = '/static/newImg/avatar/default.webp';
      let src = (item && item.avatar) ? item.avatar : '';
      if (!src) return DEFAULT_AVATAR;
      try {
        if (this.$imgUrl && typeof this.$imgUrl === 'function') {
          return this.$imgUrl(src);
        }
      } catch (e) {}
      return src;
    },
    onAvatarError(e) {
      const DEFAULT_AVATAR = '/static/newImg/avatar/default.webp';
      const img = e && e.target;
      if (!img) return;
      img.onerror = null;
      img.removeAttribute('onerror');
      if (img.src && img.src.indexOf('avatar/default.webp') === -1) {
        img.src = DEFAULT_AVATAR;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../static/css/common.css';

.subinfo-page {
  min-height: 100vh;
  background: #1E1F24;
  padding-bottom: 1rem;
  position: relative;
}

/* ========== 顶部筛选条 + 悬浮下拉：完全对齐 WithdrawRecord.vue 样式 ========== */
.subinfo-filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.15rem;
  margin: 0 0.2rem 0.2rem;
  padding-top: 0.2rem;
  position: relative;
  z-index: 10;
}

.subinfo-filters {
  display: flex;
  gap: 0.15rem;
  flex-shrink: 0;
}

.subinfo-filter {
  display: flex;
  align-items: center;
  gap: 0.05rem;
  padding: 0.1rem 0.2rem;
  border: 1px solid #555;
  border-radius: 0.3rem;
  font-size: 0.3rem;
  color: #ccc;
  cursor: pointer;
  white-space: nowrap;
}

.subinfo-search {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  background: #292A2C;
  border: 1px solid #555;
  border-radius: 0.3rem;
  padding: 0 0.15rem;
  height: 0.54rem;

  .subinfo-search-input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 0.26rem;
    &::placeholder { color: #888; }
  }
  .subinfo-search-icon { flex-shrink: 0; }
}

/* 日期下拉弹框（悬浮：直接挂在 .subinfo-filter-bar 下，紧贴按钮下方；不遮整屏） */
.subinfo-date-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #222;
  border-radius: 0.15rem;
  padding: 0.3rem;
  z-index: 100;
}

/* 一行 3 个按钮：今日/昨日/本周 · 上周/本月/上月 · 全部(单独一行1/3宽) */
.subinfo-date-options {
  display: flex;
  flex-wrap: wrap;
  row-gap: 0.15rem;
  justify-content: flex-start;
  margin-bottom: 0.2rem;

  .subinfo-date-option {
    /* 一行 3 个，统一宽度 30% 对齐 WithdrawRecord.vue 今日昨日全部按钮宽度 */
    width: 30%;
    margin-right: 5%;
    text-align: center;
    padding: 0.25rem 0;
    border: 1px solid #555;
    border-radius: 0.1rem;
    font-size: 0.32rem;
    color: #fff;
    cursor: pointer;

    &.active {
      border-color: #DFB372;
      color: #DFB372;
    }

    /* 每 3 个一组，第 3n 个不 margin-right；第 7 个「全部」最后一行保持 30% 左对齐 */
    &:nth-child(3n) {
      margin-right: 0;
    }
    &:last-child {
      width: 30%;
      margin-right: auto;
    }
  }
}

.subinfo-date-custom-label {
  font-size: 0.3rem;
  color: #fff;
  margin-bottom: 0.15rem;
}
.subinfo-date-range {
  display: flex;
  gap: 0.2rem;
  margin-bottom: 0.2rem;
}
.subinfo-date-range-col { flex: 1; }
.subinfo-date-range-title {
  text-align: center;
  font-size: 0.35rem;
  color: #999;
  margin-bottom: 0.1rem;
}
.subinfo-date-picker {
  display: flex;
  height: 2.4rem;
  position: relative;
  overflow: hidden;
}
.subinfo-date-picker-col {
  flex: 1;
  position: relative;
  height: 100%;
}
.subinfo-date-picker-list {
  position: absolute;
  left: 0; right: 0; top: 0;
  transition: transform 0.3s ease;
}
.subinfo-date-picker-item {
  height: 0.6rem;
  line-height: 0.6rem;
  font-size: 0.26rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &.selected {
    color: #fff;
    font-size: 0.35rem;
    font-weight: bold;
  }
}
.subinfo-date-picker-mask {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(34,34,34,0.9) 0%,
    rgba(34,34,34,0) 35%,
    rgba(34,34,34,0) 65%,
    rgba(34,34,34,0.9) 100%
  );
}
.subinfo-date-picker-frame {
  position: absolute;
  top: 0.9rem; left: 0; right: 0;
  height: 0.6rem;
  pointer-events: none;
  border-top: 1px solid #444;
  border-bottom: 1px solid #444;
}
.subinfo-date-btns {
  display: flex;
  gap: 0.2rem;
}
.subinfo-date-btn {
  flex: 1;
  text-align: center;
  padding: 0.25rem 0;
  border-radius: 0.1rem;
  font-size: 0.35rem;
  cursor: pointer;
}
.subinfo-date-btn-cancel {
  border: 1px solid #DFB372;
  color: #DFB372;
}
.subinfo-date-btn-confirm {
  background: #DFB372;
  color: #1a1a1a;
}

/* 排序下拉（参考提现记录状态下拉：短、窄、position absolute 相对筛选条；不遮全屏；状态下拉 left=1.6rem 这里让它相对第 2 个按钮靠右就行） */
.subinfo-sort-dropdown {
  position: absolute;
  top: 100%;
  left: 1.6rem;
  width: 3rem;
  background: #222;
  border-radius: 0.15rem;
  padding: 0.1rem 0;
  z-index: 100;
  max-height: 3rem;
  overflow-y: auto;
}
.subinfo-sort-option {
  padding: 0.2rem 0.3rem;
  font-size: 0.35rem;
  color: #999;
  cursor: pointer;

  &.active {
    color: #DFB372;
  }
}

/* ========== 列表 ========== */
.list-wrap {
  max-height: calc(100vh - 2.2rem);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  .sub-list {
    padding: 0.2rem;

    .sub-item {
      background: #292A2C;
      border-radius: 0.12rem;
      padding: 0.2rem;
      margin-bottom: 0.15rem;
      display: flex;
      flex-direction: column;

      .sub-top {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .sub-left {
          display: flex;
          align-items: center;
          gap: 0.15rem;

          .avatar-wrap {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;

            .avatar {
              width: 1.2rem;
              height: 1.2rem;
              border-radius: 50%;
              display: block;
            }

            .vip-badge {
              position: absolute;
              top: -0.08rem;
              left: -0.08rem;
              background: #E84040;
              color: #fff;
              font-size: 0.18rem;
              padding: 0.02rem 0.08rem;
              border-radius: 0.1rem;
              white-space: nowrap;
            }
          }

          .sub-info {
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 0.06rem;

            .sub-id {
              display: flex;
              align-items: center;
              gap: 0.08rem;

              .id-text {
                font-size: 0.28rem;
                color: #DFB372;
              }
              .status-dot {
                width: 0.1rem; height: 0.1rem;
                border-radius: 50%;
                background: #888;
                &.online { background: #3CC82F; }
              }
              .status-text {
                font-size: 0.22rem;
                color: #888;
              }
            }

            .sub-row {
              display: flex;
              align-items: center;
              gap: 0.08rem;
              font-size: 0.28rem;

              .sub-label { color: #888; font-size: 0.26rem; }
              .sub-value {
                color: #fff;
                font-size: 0.3rem;
                &.normal { color: #3CC82F; }
                &.ban    { color: #E84040; }
              }
            }
          }
        }
      }

      .sub-right {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0.06rem;
        text-align: right;
        margin-top: 0.06rem;

        .right-row {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.1rem;

          .label { font-size: 0.26rem; color: #888; }
          .value {
            font-size: 0.3rem;
            color: #fff;
            min-width: 0.8rem;
            &.normal { color: #3CC82F; }
            &.ban    { color: #E84040; }
          }
        }
      }
    }
  }

  .list-footer {
    padding: 0.2rem 0 1rem;
    text-align: center;

    .load-more {
      display: inline-block;
      padding: 0.14rem 0.5rem;
      border: 1px solid #DFB372;
      border-radius: 0.4rem;
      color: #DFB372;
      font-size: 0.24rem;
      cursor: pointer;

      .lm-text { letter-spacing: 0.02rem; }
    }
    .list-tip {
      color: #888;
      font-size: 0.24rem;
      padding: 0.1rem 0;
    }
  }

  .empty-state {
    text-align: center;
    padding: 3rem 0;
    color: #666;

    .empty-icon {
      width: 3rem;
      height: 3rem;
    }
    .empty-text {
      font-size: 0.35rem;
      margin: 0.2rem 0 0;
      line-height: 1.6;
      .empty-link-gray { color: #666; }
      .empty-link      { color: #d1ae52; }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/component/promote/SubInfo.vue